import React, { useEffect, useRef, useState } from 'react';
import { Formik, Form } from 'formik';
import { Alert, Button, ButtonGroup, Container, Row, Col } from 'reactstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  useTranslator,
  useToast,
  useAppContext,
  callNativeAppRemoveLastRoute,
  FormikInput,
  FormikSelect,
} from '@front-activerse/common-components';
import Layout from '../../Layout/Layout';
import TitleBar from '../../Layout/TitleBar';
import TrackerFeedback from '../components/TrackerFeedback';
import { Ingredient } from '../types';
import MacroFields from './MacroFields';
import validationSchema from './validationSchema';
import {
  AddUserProductFormValues,
  emptyFormValues,
  toMutationInput,
  scanResultToFormValues,
  addedProductToIngredient,
  addedProductToMealData,
  caloriesMismatchRatio,
  CALORIE_MISMATCH_THRESHOLD,
  resolveErrorField,
  SCAN_LABEL_RESULT_STORAGE_KEY,
  SCAN_LABEL_RESULT_EVENT,
} from './helpers';

const MULTIPLIER_STORAGE_KEY = 'tracker_multiplier';
const ADD_INGREDIENT_RETURN_STORAGE_KEY = 'tracker_add_ingredient_return_state';
const ADD_INGREDIENT_RETURN_EVENT = 'tracker_add_ingredient_return';

interface AddUserProductLocationState {
  ingredients?: Ingredient[];
  mealName?: string;
  returnPath?: string;
  servings?: number;
  stepsBack?: number;
  ean?: string;
  autoScan?: boolean;
  // Set when entering from the standalone barcode scanner with no meal in progress: after saving,
  // create a new meal containing this product and open the meal editor (instead of navigate(-1),
  // which would fall back onto the scanner). `fromUserMeals` picks the meal-editor base path.
  createMealFromProduct?: boolean;
  fromUserMeals?: boolean;
}

interface TrackerAddUserProductContainerProps {
  addUserProduct: (input: ReturnType<typeof toMutationInput>) => Promise<unknown>;
}

const TrackerAddUserProductContainer: React.FC<TrackerAddUserProductContainerProps> = ({
  addUserProduct,
}) => {
  const { t } = useTranslator();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = (location.state as AddUserProductLocationState | null) ?? {};
  const { toastError, toastSuccess } = useToast();
  const {
    images: { aiBadgeTiny, scanLabelIcon },
  } = useAppContext();

  const [initialValues, setInitialValues] = useState<AddUserProductFormValues>(emptyFormValues);
  const [isAiPrefilled, setIsAiPrefilled] = useState(false);
  const [scanRequestId, setScanRequestId] = useState<string | null>(null);
  // Two-step save when stated calories drift too far from the Atwater estimate: the first submit
  // surfaces a warning and arms confirmation; the second submit (button now "Confirm and save")
  // goes through. Reset whenever the macro/mode inputs change so a fresh edit re-checks.
  const [calorieWarningArmed, setCalorieWarningArmed] = useState(false);
  // Scroll the confirm button into view when the mismatch warning appears — the inserted Alert
  // otherwise pushes the (now "Confirm and save") button below the fold and the user can't see it.
  const actionsRef = useRef<HTMLDivElement>(null);

  const multiplier = parseFloat(sessionStorage.getItem(MULTIPLIER_STORAGE_KEY) || '1');

  useEffect(() => {
    // Guard scrollIntoView: jsdom (test env) doesn't implement it.
    if (calorieWarningArmed && typeof actionsRef.current?.scrollIntoView === 'function') {
      actionsRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [calorieWarningArmed]);

  const applyScanPayload = (payload: any) => {
    if (
      payload?.primaryIngredient &&
      typeof payload.primaryIngredient === 'object' &&
      typeof payload.primaryIngredient.name === 'string'
    ) {
      setInitialValues((current) =>
        scanResultToFormValues(payload.primaryIngredient, payload.formValues ?? current)
      );
      setIsAiPrefilled(true);
      if (typeof payload.requestId === 'string') setScanRequestId(payload.requestId);
    }
  };

  // Primary consumption path for the ScanLabel result: SlideRoutes renders with
  // `destroy={false}`, so this page stays mounted (with a frozen location) while the scanner is
  // on top — a mount effect alone never re-runs when the scanner navigates back. The scanner
  // dispatches a CustomEvent right before leaving; consume it here and clear the storage copy.
  useEffect(() => {
    const handleScanResult = (event: Event) => {
      sessionStorage.removeItem(SCAN_LABEL_RESULT_STORAGE_KEY);
      applyScanPayload((event as CustomEvent).detail);
    };
    window.addEventListener(SCAN_LABEL_RESULT_EVENT, handleScanResult);
    return () => window.removeEventListener(SCAN_LABEL_RESULT_EVENT, handleScanResult);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Consume a scan result left by the ScanLabel flow (fallback for a fresh mount — e.g. full
  // page reload right after scanning), or — when there is none — honor an action-sheet
  // `autoScan` entry by jumping straight into the scanner.
  // Both branches must live in a single mount effect: reading+deleting the stored result in one
  // effect and checking `autoScan` in a second effect races, because the first effect has
  // already deleted the key by the time the second one checks for it — causing the scanner to
  // re-launch every time the user returns from it (including on Cancel).
  useEffect(() => {
    const raw = sessionStorage.getItem(SCAN_LABEL_RESULT_STORAGE_KEY);
    if (raw) {
      sessionStorage.removeItem(SCAN_LABEL_RESULT_STORAGE_KEY);
      try {
        applyScanPayload(JSON.parse(raw));
      } catch {
        // ignore malformed payloads
      }
    } else if (locationState.autoScan) {
      // Clear `autoScan` from history state first so that returning here (e.g. Cancel from the
      // scanner triggers `navigate(-1)` back to this same history entry) lands on a plain empty
      // form instead of re-triggering the scanner.
      navigate(location.pathname, { replace: true, state: { ...locationState, autoScan: false } });
      navigate('scan-label', { state: { formValues: emptyFormValues } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Any edit to macros or a mode switch invalidates a pending calorie-mismatch confirmation.
  const resetCalorieWarning = () => {
    if (calorieWarningArmed) setCalorieWarningArmed(false);
  };

  const goBackToMeal = () => {
    const stepsBack = locationState.stepsBack ?? 1;
    for (let i = 0; i < stepsBack; i += 1) {
      callNativeAppRemoveLastRoute();
    }
    navigate(-stepsBack);
  };

  const handleSubmit = async (
    values: AddUserProductFormValues,
    {
      setFieldError,
      setFieldTouched,
      setSubmitting,
    }: {
      setFieldError: (field: string, message: string) => void;
      setFieldTouched: (field: string, touched?: boolean, shouldValidate?: boolean) => void;
      setSubmitting: (v: boolean) => void;
    }
  ) => {
    // Atwater cross-check: if stated calories drift > threshold from the macro-derived estimate,
    // block the first submit with a warning and require an explicit second confirm.
    const mismatch = caloriesMismatchRatio(values);
    if (!calorieWarningArmed && mismatch != null && mismatch > CALORIE_MISMATCH_THRESHOLD) {
      setCalorieWarningArmed(true);
      setSubmitting(false);
      return;
    }

    try {
      const result: any = await addUserProduct(toMutationInput(values, locationState.ean));

      if (result?.__typename === 'AddedUserProduct') {
        if (locationState.returnPath) {
          const ingredient = addedProductToIngredient(result, values, multiplier);
          const payload = {
            returnPath: locationState.returnPath,
            ingredients: [...(locationState.ingredients ?? []), ingredient],
            mealName: locationState.mealName ?? '',
            ...(locationState.servings != null && { servings: locationState.servings }),
            updatedAt: Date.now(),
          };
          sessionStorage.setItem(ADD_INGREDIENT_RETURN_STORAGE_KEY, JSON.stringify(payload));
          window.dispatchEvent(new CustomEvent(ADD_INGREDIENT_RETURN_EVENT, { detail: payload }));
          toastSuccess(<p>{t('tracker.meal.ingredients.addedConfirmation')}</p>);
          goBackToMeal();
        } else if (locationState.createMealFromProduct) {
          // Entered from the standalone barcode scanner with no meal in progress: build a brand-new
          // meal containing just this product and open the meal editor, mirroring the found-product
          // path. navigate(-1) here would land back on the scanner.
          const ingredient = addedProductToIngredient(result, values, multiplier);
          const dietMealId = Math.floor(Math.random() * 1000000000);
          const mealData = addedProductToMealData(ingredient, dietMealId);
          sessionStorage.setItem('tracker_depth', '2');
          const basePath = locationState.fromUserMeals
            ? '/meals/user-meals/describe-meal/meal-details'
            : '/tracker/describe-meal/meal-details';
          navigate(`${basePath}/${dietMealId}`, {
            state: { mealData, timestamp: Date.now() },
            replace: true,
          });
        } else {
          toastSuccess(<p>{t('tracker.meal.ingredients.addedConfirmation')}</p>);
          callNativeAppRemoveLastRoute();
          navigate(-1);
        }
      } else if (result?.__typename === 'ValidationException') {
        (result.details ?? []).forEach((detail: { fieldName: string; messages: string[] }) => {
          const field = resolveErrorField(detail.fieldName, values.mode);
          if (field && detail.messages?.length) {
            // FormikInput only renders an error once the field is touched; a server-side
            // ValidationException can land on a field the user never blurred, so mark it
            // touched (without re-triggering client-side validation) alongside the error.
            setFieldTouched(field, true, false);
            setFieldError(field, detail.messages.join(' '));
          }
        });
        if (!result.details?.length && !result.messages?.length) {
          toastError();
        }
        // Non-field-level messages (e.g. general validation failures not tied to a specific
        // input) must never be silently dropped, even when field-level details were also present.
        if (result.messages?.length) {
          toastError(<p>{result.messages.join(' ')}</p>);
        }
      } else {
        toastError();
      }
    } catch {
      toastError();
    } finally {
      setSubmitting(false);
    }
  };

  const unitSuffix = (unit: AddUserProductFormValues['unit']) =>
    unit === 'MILLILITERS' ? 'ml' : 'g';

  return (
    <Layout>
      <TitleBar showBackBtn={true}>{t('tracker.addProduct.navbarTitle')}</TitleBar>

      <div className="page-template-tracker edit-meal-page">
        <Container className="py-md-4 full-height">
          <Row>
            <Col xl={{ size: 10, offset: 1 }}>
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
                validateOnMount
              >
                {({ values, setFieldValue, setFieldTouched, isValid, isSubmitting }) => {
                  // Editing a macro field: clear any pending calorie-mismatch confirmation AND
                  // mark the field touched so a format/limit error shows immediately, instead of
                  // only after the input loses focus (Formik touches on blur by default).
                  const onMacroChange = (fieldPath: string) => {
                    resetCalorieWarning();
                    // Mark touched so a format/limit error shows immediately (Formik touches on
                    // blur by default). shouldValidate=false: Formik already re-validates on the
                    // value change; passing true here would validate against the pre-update value
                    // (setValue is batched) and wrongly keep the field in an error state.
                    setFieldTouched(fieldPath, true, false);
                  };
                  // Switching mode: clear the calorie warning and reset the touched state of the
                  // mode we're switching TO — but ONLY for fields that are still empty. An empty
                  // required field shouldn't flash red just because the user visited it before,
                  // yet a field the user actually filled with an invalid value must KEEP its error
                  // across mode round-trips (100g → serving → 100g). So we only untouch blanks.
                  const switchMode = (mode: AddUserProductFormValues['mode']) => {
                    setFieldValue('mode', mode);
                    resetCalorieWarning();
                    const group = mode === 'PER_SERVING' ? 'perServing' : 'per100';
                    const targetMacros = mode === 'PER_SERVING' ? values.perServing : values.per100;
                    (['calories', 'protein', 'fat', 'carbs', 'fiber'] as const).forEach((f) => {
                      if (targetMacros[f].trim() === '')
                        setFieldTouched(`${group}.${f}`, false, false);
                    });
                    if (mode === 'PER_SERVING' && values.servingSize.trim() === '')
                      setFieldTouched('servingSize', false, false);
                  };
                  return (
                    <Form noValidate data-testid="add-product-form">
                      <div className="tracker-container">
                        <h2 className="title-small semi-bold mb-m">
                          {t('addIngredient.productDetails.title')}
                        </h2>

                        <FormikInput
                          name="name"
                          label={t('addIngredient.productName')}
                          translateErrMsg
                          data-testid="add-product-name"
                          data-test-id="add_product_name"
                        />
                        <FormikInput
                          name="brand"
                          label={t('addIngredient.brandName')}
                          translateErrMsg
                          data-testid="add-product-brand"
                          data-test-id="add_product_brand"
                        />

                        <Button
                          type="button"
                          color="secondary"
                          outline
                          className="w-100 mb-l"
                          onClick={() => navigate('scan-label', { state: { formValues: values } })}
                          data-testid="add-product-scan-label"
                          data-test-id="add_product_scan_label"
                        >
                          <img src={scanLabelIcon} alt="" className="me-2" />
                          {isAiPrefilled
                            ? t('tracker.addProduct.rescanLabel.button')
                            : t('tracker.addProduct.scanLabel.button')}
                        </Button>

                        <h2 className="title-small semi-bold mb-m">
                          {t('addIngredient.nutritionValues.title')}
                        </h2>

                        <FormikSelect
                          name="unit"
                          label={t('tracker.addProduct.unit.label')}
                          translateErrMsg
                          data-testid="add-product-unit"
                          data-test-id="add_product_unit"
                        >
                          <option value="GRAMS">{t('tracker.addProduct.unit.grams')}</option>
                          <option value="MILLILITERS">
                            {t('tracker.addProduct.unit.milliliters')}
                          </option>
                        </FormikSelect>

                        <ButtonGroup className="w-100 mb-m">
                          <Button
                            type="button"
                            className="w-50"
                            color={values.mode === 'PER_100_UNITS' ? 'primary' : 'secondary'}
                            outline={values.mode !== 'PER_100_UNITS'}
                            onClick={() => switchMode('PER_100_UNITS')}
                            data-testid="add-product-mode-per-100"
                          >
                            {values.unit === 'MILLILITERS'
                              ? t('tracker.addProduct.mode.per100ml')
                              : t('tracker.addProduct.mode.per100g')}
                          </Button>
                          <Button
                            type="button"
                            className="w-50"
                            color={values.mode === 'PER_SERVING' ? 'primary' : 'secondary'}
                            outline={values.mode !== 'PER_SERVING'}
                            onClick={() => switchMode('PER_SERVING')}
                            data-testid="add-product-mode-per-serving"
                          >
                            {t('tracker.addProduct.mode.perServing')}
                          </Button>
                        </ButtonGroup>

                        {values.mode === 'PER_SERVING' && (
                          <FormikInput
                            name="servingSize"
                            label={t('tracker.addProduct.servingSize.label')}
                            unit={unitSuffix(values.unit)}
                            inputMode="decimal"
                            maxLength={6}
                            translateErrMsg
                            data-testid="add-product-serving-size"
                            data-test-id="add_product_serving_size"
                          />
                        )}

                        <MacroFields mode={values.mode} onFieldChange={onMacroChange} />

                        {calorieWarningArmed && (
                          <Alert
                            color="warning"
                            fade={false}
                            className="mt-l"
                            data-testid="add-product-calorie-warning"
                          >
                            <p>{t('tracker.addProduct.caloriesMismatch.warning')}</p>
                          </Alert>
                        )}

                        <div ref={actionsRef}>
                          <Row className="mt-l">
                            <Col xs="6">
                              <Button
                                type="button"
                                color="secondary"
                                outline
                                className="w-100"
                                onClick={() => navigate(-1)}
                                data-testid="add-product-cancel"
                                data-test-id="add_product_cancel"
                              >
                                {t('common.cancel')}
                              </Button>
                            </Col>
                            <Col xs="6">
                              <Button
                                type="submit"
                                color="primary"
                                className="w-100"
                                disabled={!isValid || isSubmitting}
                                data-testid="add-product-save"
                                data-test-id="add_product_save"
                              >
                                {calorieWarningArmed
                                  ? t('tracker.addProduct.confirmSave.button')
                                  : t('tracker.saveButton')}
                              </Button>
                            </Col>
                          </Row>
                        </div>

                        {isAiPrefilled && (
                          <p className="description text-text-variant-color mt-l mb-0">
                            <img src={aiBadgeTiny} alt="" className="me-1" />
                            {t('tracker.aiSignature')}
                          </p>
                        )}

                        {/* Rate the scan-label AI result (same thumbs component as scan-plate) */}
                        {scanRequestId && <TrackerFeedback requestId={scanRequestId} />}
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </Col>
          </Row>
        </Container>
      </div>
    </Layout>
  );
};

export default TrackerAddUserProductContainer;
